import React, { Suspense, lazy, useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { Layout, Space, Button, Spin, Result } from 'antd';
import { UserOutlined, LogoutOutlined, MenuOutlined } from '@ant-design/icons';
import Sidebar from './components/Sidebar';
import NotificationBell from './components/NotificationBell';
import ChangePassword from './components/ChangePassword';
import ErrorBoundary from './components/ErrorBoundary';
import { ViewAsBanner, ViewAsButton } from './components/ViewAs';
import PrintHeader from './components/PrintHeader';
import HelpButton from './components/HelpButton';
import TipsSwitch from './components/TipsSwitch';
import IdleLogout from './components/IdleLogout';
import PresenceHeartbeat from './components/PresenceHeartbeat';
import Login from './pages/Login';
import api, { clearSession, currentUser } from './utils/api';
import useMobile from './utils/useMobile';
import { landingPath, roleLabel, loadScreens, canSee } from './utils/roles';

const LPOPage = lazy(() => import('./pages/LPOPage'));
const PurchaseRequestsPage = lazy(() => import('./pages/PurchaseRequestsPage'));
const TreatmentsPage = lazy(() => import('./pages/TreatmentsPage'));
const GoodsReceiptsPage = lazy(() => import('./pages/GoodsReceiptsPage'));
const SupplierInvoicesPage = lazy(() => import('./pages/SupplierInvoicesPage'));
const MyTasksPage = lazy(() => import('./pages/MyTasksPage'));
const ReportsPage = lazy(() => import('./pages/ReportsPage'));
const DistributionPage = lazy(() => import('./pages/DistributionPage'));
const VarianceReportPage = lazy(() => import('./pages/VarianceReportPage'));
const FeedPlanPage = lazy(() => import('./pages/FeedPlanPage'));
const BeddingPage = lazy(() => import('./pages/BeddingPage'));
const FeedSheetPage = lazy(() => import('./pages/FeedSheetPage'));
const NightWeighPage = lazy(() => import('./pages/NightWeighPage'));
const FeedTransferPage = lazy(() => import('./pages/FeedTransferPage'));
const FeedVarianceReportPage = lazy(() => import('./pages/FeedVarianceReportPage'));
const ServiceRecordsPage = lazy(() => import('./pages/ServiceRecordsPage'));
const ServiceCostDashboardPage = lazy(() => import('./pages/ServiceCostDashboardPage'));
const ServiceSchedulesPage = lazy(() => import('./pages/ServiceSchedulesPage'));
const HorsesPage = lazy(() => import('./pages/HorsesPage'));
const HorseStatisticsPage = lazy(() => import('./pages/HorseStatisticsPage'));
const SchoolSetupPage = lazy(() => import('./pages/SchoolSetupPage'));
const ClubSysImportPage = lazy(() => import('./pages/ClubSysImportPage'));
const TimetablePage = lazy(() => import('./pages/TimetablePage'));
const SecurityGatePage = lazy(() => import('./pages/SecurityGatePage'));
const SchoolControlPage = lazy(() => import('./pages/SchoolControlPage'));
const CoachDayPage = lazy(() => import('./pages/CoachDayPage'));
const LiveryPage = lazy(() => import('./pages/LiveryPage'));
const HorseActivitiesPage = lazy(() => import('./pages/HorseActivitiesPage'));
const HorseCostRevenuePage = lazy(() => import('./pages/HorseCostRevenuePage'));
const StockControlPage = lazy(() => import('./pages/StockControlPage'));
const HorseProfilePage = lazy(() => import('./pages/HorseProfilePage'));
const HorseListsPage = lazy(() => import('./pages/HorseListsPage'));
const HorseHealthPage = lazy(() => import('./pages/HorseHealthPage'));
const HorseMovementsPage = lazy(() => import('./pages/HorseMovementsPage'));
const PonyRidesPage = lazy(() => import('./pages/PonyRidesPage'));
const DayEndPage = lazy(() => import('./pages/DayEndPage'));
const ClientReviewPage = lazy(() => import('./pages/ClientReviewPage'));
const CompensationsPage = lazy(() => import('./pages/CompensationsPage'));
const OpeningBalancesPage = lazy(() => import('./pages/OpeningBalancesPage'));
const CostCentresPage = lazy(() => import('./pages/CostCentresPage'));
const HorseMonthComparePage = lazy(() => import('./pages/HorseMonthComparePage'));
const ScreensPage = lazy(() => import('./pages/ScreensPage'));
const CashierSalesPage = lazy(() => import('./pages/CashierSalesPage'));
const PrintSettingsPage = lazy(() => import('./pages/PrintSettingsPage'));
const WhoIsUsingPage = lazy(() => import('./pages/WhoIsUsingPage'));
const ItemsPage = lazy(() => import('./pages/ItemsPage'));

const { Content, Header } = Layout;

const RequireAuth = ({ children }) => {
  const location = useLocation();
  if (!localStorage.getItem('token')) {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }
  const user = currentUser();
  if (user?.must_change_password && !user?.preview) {
    return <ChangePassword forced />;
  }
  return children;
};

const useLogout = () => {
  const navigate = useNavigate();
  return () => {
    clearSession();
    navigate('/login', { replace: true });
  };
};

const userLabel = (user) => (user ? `${user.first_name || user.username}${user.role ? ` (${roleLabel(user)})` : ''}` : '');

/** Account buttons shown at the bottom of the phone menu instead of the top bar. */
const MobileAccount = () => {
  const user = currentUser();
  const logout = useLogout();
  return (
    <Space direction="vertical" style={{ width: '100%' }}>
      <span><UserOutlined /> {userLabel(user)}</span>
      <Space wrap>
        <TipsSwitch />
        <ViewAsButton />
        {!user?.preview && <ChangePassword />}
        <Button size="small" icon={<LogoutOutlined />} onClick={logout}>Logout</Button>
      </Space>
    </Space>
  );
};

const TopBar = ({ mobile, onMenu }) => {
  const user = currentUser();
  const logout = useLogout();
  if (mobile) {
    return (
      <Header style={{ background: '#fff', padding: '0 8px', display: 'flex', alignItems: 'center', gap: 8, borderBottom: '1px solid #f0f0f0', position: 'sticky', top: 0, zIndex: 10 }}>
        <Button type="text" icon={<MenuOutlined style={{ fontSize: 20 }} />} onClick={onMenu} aria-label="Menu" />
        <b style={{ flex: 1, fontSize: 16 }}>ASHR</b>
        <HelpButton />
        <NotificationBell />
      </Header>
    );
  }
  return (
    <Header style={{ background: '#fff', padding: '0 16px', display: 'flex', justifyContent: 'flex-end', alignItems: 'center', borderBottom: '1px solid #f0f0f0' }}>
      <Space>
        <TipsSwitch />
        <HelpButton />
        <NotificationBell />
        <UserOutlined />
        <span>{userLabel(user)}</span>
        <ViewAsButton />
        {!user?.preview && <ChangePassword />}
        <Button size="small" icon={<LogoutOutlined />} onClick={logout}>Logout</Button>
      </Space>
    </Header>
  );
};

const PageLoading = () => (
  <div style={{ textAlign: 'center', padding: 80 }}><Spin size="large" /></div>
);

/* Screens the Admin hid for the user's roles (Screens & Roles) are not opened, even from a bookmark. */
const hiddenScreen = (pathname) => {
  const path = pathname.startsWith('/horses/') ? '/horses' : pathname;
  if (canSee(path)) return null;
  const home = landingPath();
  return home !== path && canSee(home)
    ? <Navigate to={home} replace />
    : <Result status="403" title="This screen is not available for your role" />;
};

function MainLayout() {
  const location = useLocation();
  const [screensVer, setScreensVer] = useState(0);
  useEffect(() => {
    // Refresh profile so must_change_password and roles match the server (avoids a blank/broken session).
    api.get('/auth/users/me/').then((r) => {
      localStorage.setItem('user', JSON.stringify(r.data));
      if (r.data.must_change_password) window.location.replace('/');
    }).catch(() => {});
    loadScreens().then(() => setScreensVer((v) => v + 1)).catch(() => {});
  }, []);
  const mobile = useMobile();
  const [menuOpen, setMenuOpen] = useState(false);
  const hidden = hiddenScreen(location.pathname);
  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Sidebar key={screensVer} mobile={mobile} open={menuOpen} onClose={() => setMenuOpen(false)} footer={mobile && <MobileAccount />} />
      <Layout>
        <IdleLogout />
        <PresenceHeartbeat />
        <ViewAsBanner />
        <TopBar mobile={mobile} onMenu={() => setMenuOpen(true)} />
        <Content className={mobile ? 'mobile-content' : undefined}
          style={mobile ? { margin: 0, padding: 8, background: '#fff' } : { margin: '24px 16px', padding: 24, background: '#fff' }}>
          <PrintHeader />
          <ErrorBoundary key={location.pathname}>
            <Suspense fallback={<PageLoading />}>
              {hidden || (
              <Routes>
                <Route path="/" element={<Navigate to={landingPath()} replace />} />
                <Route path="/lpos" element={<LPOPage />} />
                <Route path="/purchase-requests" element={<PurchaseRequestsPage />} />
                <Route path="/treatments" element={<TreatmentsPage />} />
                <Route path="/goods-receipts" element={<GoodsReceiptsPage />} />
                <Route path="/supplier-invoices" element={<SupplierInvoicesPage />} />
                <Route path="/my-tasks" element={<MyTasksPage />} />
                <Route path="/reports" element={<ReportsPage />} />
                <Route path="/reports/:slug" element={<ReportsPage />} />
                <Route path="/distribution" element={<DistributionPage />} />
                <Route path="/variance-report" element={<VarianceReportPage />} />
                <Route path="/feed-plan" element={<FeedPlanPage />} />
                <Route path="/bedding" element={<BeddingPage />} />
                <Route path="/feed-sheet" element={<FeedSheetPage />} />
                <Route path="/feed-night-weigh" element={<NightWeighPage />} />
                <Route path="/feed-transfer" element={<FeedTransferPage />} />
                <Route path="/feed-variance-report" element={<FeedVarianceReportPage />} />
                <Route path="/service-records" element={<ServiceRecordsPage />} />
                <Route path="/service-schedules" element={<ServiceSchedulesPage />} />
                <Route path="/service-cost-dashboard" element={<ServiceCostDashboardPage />} />
                <Route path="/horses" element={<HorsesPage />} />
                <Route path="/horses/:id" element={<HorseProfilePage />} />
                <Route path="/horse-health" element={<HorseHealthPage />} />
                <Route path="/horse-movements" element={<HorseMovementsPage />} />
                <Route path="/pony-rides" element={<PonyRidesPage />} />
                <Route path="/setup/horse-lists" element={<HorseListsPage />} />
                <Route path="/horse-statistics" element={<HorseStatisticsPage />} />
                <Route path="/horse-month-compare" element={<HorseMonthComparePage />} />
                <Route path="/school-setup" element={<SchoolSetupPage />} />
                <Route path="/clubsys-import" element={<ClubSysImportPage />} />
                <Route path="/timetable" element={<TimetablePage />} />
                <Route path="/gate" element={<SecurityGatePage />} />
                <Route path="/school-control" element={<SchoolControlPage />} />
                <Route path="/school/day-end" element={<DayEndPage />} />
                <Route path="/school/client-review" element={<ClientReviewPage />} />
                <Route path="/school/compensations" element={<CompensationsPage />} />
                <Route path="/school/opening" element={<OpeningBalancesPage />} />
                <Route path="/cost-centres" element={<CostCentresPage />} />
                <Route path="/coach-day" element={<CoachDayPage />} />
                <Route path="/livery" element={<LiveryPage />} />
                <Route path="/horse-activities" element={<HorseActivitiesPage />} />
                <Route path="/horse-cost-revenue" element={<HorseCostRevenuePage />} />
                <Route path="/stock-control" element={<StockControlPage />} />
                <Route path="/cashier-sales" element={<CashierSalesPage />} />
                <Route path="/print-settings" element={<PrintSettingsPage />} />
                <Route path="/who-is-using" element={<WhoIsUsingPage />} />
                <Route path="/items" element={<ItemsPage />} />
                <Route path="/screens" element={<ScreensPage onSaved={() => setScreensVer((v) => v + 1)} />} />
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
              )}
            </Suspense>
          </ErrorBoundary>
        </Content>
      </Layout>
    </Layout>
  );
}

function App() {
  return (
    <Router>
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/*" element={<RequireAuth><MainLayout /></RequireAuth>} />
      </Routes>
    </Router>
  );
}

export default App;
